import React, { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FaPaperPlane, FaTimes } from 'react-icons/fa';

const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com',
  'live.com', 'msn.com', 'yahoo.com', 'yahoo.se', 'yahoo.co.uk',
  'mail.com', 'protonmail.com', 'icloud.com', 'aol.com', 'zoho.com',
  'yandex.com', 'yandex.se', 'gmx.com', 'gmx.se', 'fastmail.com'
];

function generateCaptcha() {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { question: `${a} + ${b} = ?`, answer: a + b };
}

function ContactModal({ show, onHide }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (name === 'email') {
      const domain = value.split('@')[1]?.toLowerCase();
      if (domain && FREE_EMAIL_DOMAINS.includes(domain)) {
        setEmailError('Please use your business email address.');
      } else {
        setEmailError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (parseInt(captchaInput) !== captcha.answer) {
      setError('Incorrect answer. Please try again.');
      setCaptcha(generateCaptcha());
      setCaptchaInput('');
      return;
    }

    if (emailError) return;

    setLoading(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setFormData({ name: '', email: '', company: '', message: '' });
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
    setError('');
    setEmailError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered className="contact-modal">
      <Modal.Header closeButton closeButtonProps={{ style: { filter: 'invert(1)' } }}>
        <Modal.Title style={{ color: '#fff' }}>Get Started</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {submitted ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'var(--md-sys-color-tertiary-container)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <FaPaperPlane size={32} style={{ color: 'var(--md-sys-color-on-tertiary-container)' }} />
            </div>
            <h3 style={{ marginBottom: '1rem', color: '#fff' }}>Message Sent!</h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: 0 }}>
              Thank you for reaching out. Our team will get back to you within 24 hours.
            </p>
            <Button variant="primary" onClick={handleClose} className="mt-4">
              Close
            </Button>
          </div>
        ) : (
          <>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1.5rem' }}>
              Tell us about your AI infrastructure needs and we'll be in touch soon.
            </p>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label" style={{ color: '#fff' }}>Full Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    borderRadius: 'var(--radius-small)'
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="form-label" style={{ color: '#fff' }}>Email Address *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  isInvalid={!!emailError}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    borderRadius: 'var(--radius-small)'
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {emailError}
                </Form.Control.Feedback>
                <Form.Text style={{ color: '#888', fontSize: '0.8rem' }}>
                  Business email required
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="form-label" style={{ color: '#fff' }}>Company</Form.Label>
                <Form.Control
                  type="text"
                  name="company"
                  placeholder="Your Company"
                  value={formData.company}
                  onChange={handleChange}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    borderRadius: 'var(--radius-small)'
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label className="form-label" style={{ color: '#fff' }}>Message *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="message"
                  placeholder="Tell us about your AI infrastructure needs..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    borderRadius: 'var(--radius-small)'
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label className="form-label" style={{ color: '#fff' }}>Security Check *</Form.Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontWeight: 600, color: '#fff' }}>{captcha.question}</span>
                  <Form.Control
                    type="number"
                    placeholder="Answer"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    style={{
                      maxWidth: '100px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      borderRadius: 'var(--radius-small)',
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setCaptcha(generateCaptcha());
                      setCaptchaInput('');
                    }}
                  >
                    ↻
                  </Button>
                </div>
              </Form.Group>
              {error && (
                <div className="alert alert-danger mb-4" role="alert" style={{ background: 'rgba(220,53,69,0.2)', border: '1px solid #dc3545', color: '#f8d7da' }}>
                  {error}
                </div>
              )}
              <Button
                variant="primary"
                type="submit"
                className="w-100"
                disabled={loading || !!emailError}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </Form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ContactModal;
